import type { AppCatalogEntry, AppManifest } from '@api/apps';
import type { UserAppSummary } from '@api/appBuilder';
import { appPath } from './appRunPath';
import { builderPath, isRunnable } from './builder/userAppStatus';

export const APP_TABS = ['all', 'builtin', 'team', 'personal', 'shared'] as const;

export type AppTab = (typeof APP_TABS)[number];

export interface TabbedApp {
  readonly appKey: string;
  readonly manifest: AppManifest;
  readonly openPath: string;
  readonly tabs: readonly AppTab[];
}

/** A catalog entry without a source predates user apps, so it is built in. */
export const isBuiltInApp = (entry: AppCatalogEntry): boolean =>
  (entry.source ?? 'BUILT_IN') === 'BUILT_IN';

export const isSharedInOrg = (entry: AppCatalogEntry): boolean =>
  entry.source === 'USER' && !entry.ownedByMe && entry.visibility === 'ORG';

export const isSharedViaLink = (entry: AppCatalogEntry): boolean => Boolean(entry.viaLink);

const fromMine = (app: UserAppSummary): TabbedApp => {
  const runnable = isRunnable(app);
  return {
    appKey: app.appKey,
    manifest: app.manifest,
    openPath: runnable ? appPath(app.appKey) : builderPath(app.appKey),
    tabs: ['all', 'personal', ...(runnable && app.visibility === 'ORG' ? ['team' as const] : [])],
  };
};

const catalogTabs = (entry: AppCatalogEntry): AppTab[] => {
  const tabs: AppTab[] = ['all'];
  if (isBuiltInApp(entry)) tabs.push('builtin');
  if (entry.source === 'USER' && entry.visibility === 'ORG') tabs.push('team');
  if (entry.ownedByMe) tabs.push('personal');
  if (isSharedViaLink(entry)) tabs.push('shared');
  return tabs;
};

const fromCatalog = (entry: AppCatalogEntry): TabbedApp => ({
  appKey: entry.appKey,
  manifest: entry.manifest,
  openPath: appPath(entry.appKey),
  tabs: catalogTabs(entry),
});

const SECTION_ORDER: readonly AppTab[] = ['personal', 'team', 'shared', 'builtin'];

const sectionOf = (app: TabbedApp): number => {
  const index = SECTION_ORDER.findIndex((tab) => app.tabs.includes(tab));
  return index === -1 ? SECTION_ORDER.length : index;
};

/**
 * Every app the user can open, once each, tagged with the tabs it belongs to.
 * The user's own copy wins over the catalog's because only it knows whether a
 * draft must open in the builder.
 */
export const tabbedApps = (
  catalog: readonly AppCatalogEntry[],
  mine: readonly UserAppSummary[],
): TabbedApp[] => {
  const byKey = new Map<string, TabbedApp>(mine.map((app) => [app.appKey, fromMine(app)]));
  catalog.forEach((entry) => {
    const own = byKey.get(entry.appKey);
    const listed = fromCatalog(entry);
    byKey.set(
      entry.appKey,
      own ? { ...own, tabs: [...new Set([...own.tabs, ...listed.tabs])] } : listed,
    );
  });
  return [...byKey.values()].sort((a, b) => sectionOf(a) - sectionOf(b));
};

export const appsInTab = (apps: readonly TabbedApp[], tab: AppTab): TabbedApp[] =>
  apps.filter((app) => app.tabs.includes(tab));

export const countByTab = (apps: readonly TabbedApp[]): Record<AppTab, number> =>
  Object.fromEntries(APP_TABS.map((tab) => [tab, appsInTab(apps, tab).length])) as Record<
    AppTab,
    number
  >;
